if (!customElements.get('customization-canvas')) {
    class CustomizationCanvas extends HTMLElement {
        connectedCallback() {
            this.canvas = this.querySelector('[data-customization-canvas]');
            this.context = this.canvas.getContext('2d');
            this.dataScript = this.querySelector('#customization-canvas-data');
            this.selectedVariantId = Number(this.dataset.selectedVariantOnLoad);
            this.backgroundImage = null;

            this.canvasData = this.dataScript ? JSON.parse(this.dataScript.textContent) : null;

            this.debug = this.canvasData?.debug === true;
            this.positions = this.canvasData?.positions || [];
            this.onVariantChange = this.onVariantChange.bind( this );
            this.onCustomizationInput = this.onCustomizationInput.bind( this );

            document.addEventListener( 'variant:changed', this.onVariantChange );
            document.addEventListener( 'customization-options:input', this.onCustomizationInput );

            if ( this.debug ) {
                this.renderDebugControls();
            }

            this.loadFonts().then(() => {
                this.loadVariantImage(this.selectedVariantId);
            });
        }

        disconnectedCallback() {
            document.removeEventListener( 'variant:changed', this.onVariantChange );
            document.removeEventListener( 'customization-options:input', this.onCustomizationInput );
        }

        async loadFonts() {
            if (!document.fonts) {
                return Promise.resolve();
            }

            const fontPromises = [];

            for (let index = 0; index < this.positions.length; index++) {
                const position = this.positions[index];

                if (!position.fontFamily) {
                    continue;
                }

                if (position.fontUrl) {
                    fontPromises.push(
                        this.loadFontFromUrl(position)
                    );
                } else {
                    fontPromises.push(
                        document.fonts.load(
                            `${position.fontWeight || 400} ${position.fontSize || 16}px "${position.fontFamily}"`
                        )
                    );
                }
            }

            return Promise.all(fontPromises).then(() => {
                return document.fonts.ready;
            });
        }

        async loadFontFromUrl( position ) {
            const fontFace = new FontFace(
                position.fontFamily,
                `url(${position.fontUrl})`,
                {
                    weight: String(position.fontWeight || 400),
                    style: 'normal'
                }
            );

            return fontFace.load().then((loadedFont) => {
                document.fonts.add(loadedFont);

                return loadedFont;
            });
        }
        
        onVariantChange( event ) {
            const variantId = event.detail?.variant?.id;

            if ( variantId ) {
                this.selectedVariantId = variantId;
                this.loadVariantImage( variantId );
            }
        }

        onCustomizationInput() {
            this.render();
        }

        getVariantImageUrl( variantId ) {
            let imageUrl = null;

            if ( variantId && this.canvasData ) {
                const variants = this.canvasData.variants;
                const variantsLength = variants.length;

                for ( let index = 0; index < variantsLength; index++ ) {
                    const variant = variants[ index ];

                    if ( variant.id == variantId ) {
                        imageUrl = variant.image;
                        break;
                    }
                }
            }

            return imageUrl;
        }

        loadVariantImage( variantId ) {
            const imageUrl = this.getVariantImageUrl( variantId );

            if ( imageUrl ) {
                const image = new Image();

                image.onload = () => {
                    this.backgroundImage = image;
                    this.render();
                };

                image.src = imageUrl;
            }
        }

        getCustomizationOptions() {
            return document.querySelector('customization-options');
        }

        render() {
            this.context.clearRect(
                0,
                0,
                this.canvas.width,
                this.canvas.height
            );

            if ( this.backgroundImage ) {
                this.context.drawImage(
                    this.backgroundImage,
                    0,
                    0,
                    this.canvas.width,
                    this.canvas.height
                );
            }

            this.renderTextFields();
        }

        renderTextFields() {
            const customizationOptions = this.getCustomizationOptions();

            if ( customizationOptions ) {
                const fieldValues = customizationOptions.getFieldValues();
                const labels = Object.keys(fieldValues);
                const labelsLength = labels.length;

                for ( let index = 0; index < labelsLength; index++ ) {
                    const label = labels[index];
                    const value = fieldValues[label];
                    const position = this.positions[index];

                    if ( value && position ) {
                        this.context.font = `${position.fontWeight} ${position.fontSize}px ${position.fontFamily}`;
                        this.context.fillStyle = position.color;
                        this.context.textAlign = position.textAlign;

                        const maxWidth = position.maxWidth || 400;
                        const lineHeight = position.lineHeight || position.fontSize * 1.2;
                        const letterSpacing = position.letterSpacing || 0;
                        const maxCharactersPerLine = position.maxCharactersPerLine || null;

                        if ( this.debug && position.showBorder ) {
                            this.drawTextBox({
                                context: this.context,
                                x: position.x,
                                y: position.y,
                                maxWidth,
                                lineHeight,
                                textAlign: position.textAlign,
                                text: value,
                                letterSpacing,
                                maxCharactersPerLine
                            });
                        }

                        this.drawWrappedText({
                            context: this.context,
                            text: value,
                            x: position.x,
                            y: position.y,
                            maxWidth,
                            lineHeight,
                            letterSpacing,
                            maxCharactersPerLine
                        });
                    }
                }
            }
        }

        renderDebugControls() {
            const controls = document.createElement('div');

            controls.className = 'customization-debug-controls';
            controls.style.width = '100%';
            controls.style.maxWidth = '760px';
            controls.style.marginTop = '20px';
            controls.style.padding = '18px';
            controls.style.border = '1px solid #d9d9d9';
            controls.style.borderRadius = '12px';
            controls.style.background = '#fafafa';
            controls.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.08)';
            controls.style.fontFamily = 'Arial, sans-serif';

            controls.style.position = 'fixed';
            controls.style.right = '20px';
            controls.style.bottom = '20px';
            controls.style.zIndex = '999999';
            controls.style.width = 'min(760px, calc(100vw - 40px))';
            controls.style.maxHeight = '80vh';
            controls.style.overflow = 'auto';
            controls.style.marginTop = '0';

            controls.innerHTML = `
                <details>
                    <summary style="cursor: pointer; list-style: none;">
                        <strong style="font-size: 16px;">Debug Positions</strong>
                    </summary>

                    <div style="margin-top: 16px; margin-bottom: 16px;">
                        <button
                            type="button"
                            data-copy-all="true"
                            style="padding: 8px 12px; border: 1px solid #bbb; border-radius: 8px; background: #fff; cursor: pointer;"
                        >
                            Copy All JSON
                        </button>
                    </div>
                </details>
            `;

            for (let index = 0; index < this.positions.length; index++) {
                const position = this.positions[index];
                const details = document.createElement('details');

                details.style.marginBottom = '12px';
                details.style.padding = '14px';
                details.style.border = '1px solid #e2e2e2';
                details.style.borderRadius = '10px';
                details.style.background = '#ffffff';

                if (index === 0) {
                    details.open = true;
                }

                details.innerHTML = `
                    <summary style="display: flex; justify-content: space-between; align-items: center; gap: 12px; cursor: pointer; list-style: none;">
                        <strong>Field ${index + 1}</strong>

                        <button
                            type="button"
                            data-copy-field="${index}"
                            style="padding: 6px 10px; border: 1px solid #bbb; border-radius: 7px; background: #f7f7f7; cursor: pointer;"
                        >
                            Copy Field JSON
                        </button>
                    </summary>

                    <div style="display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; margin-top: 12px;">
                        ${this.getDebugInputMarkup(index, 'x', 'X', position.x)}
                        ${this.getDebugInputMarkup(index, 'y', 'Y', position.y)}
                        ${this.getDebugInputMarkup(index, 'fontSize', 'Font Size', position.fontSize)}
                        ${this.getDebugFontWeightMarkup(index, position.fontWeight)}
                        ${this.getDebugColorMarkup(index, position.color)}
                        ${this.getDebugInputMarkup(index, 'maxWidth', 'Max Width', position.maxWidth || 400)}
                        ${this.getDebugInputMarkup(index, 'lineHeight', 'Line Height', position.lineHeight || Math.round(position.fontSize * 1.2))}
                        ${this.getDebugInputMarkup(index, 'letterSpacing', 'Letter Spacing', position.letterSpacing || 0)}
                        ${this.getDebugInputMarkup(index, 'maxCharactersPerLine', 'Max Chars / Line', position.maxCharactersPerLine || '')}
                        ${this.getDebugFontFamilyMarkup(index, position.fontFamily)}
                        ${this.getDebugCheckboxMarkup(index, 'showBorder', 'Show Border', position.showBorder)}
                    </div>
                `;

                controls.querySelector('details').appendChild(details);
            }

            controls.addEventListener('input', (event) => {
                const input = event.target;

                if (
                    input.type !== 'number' &&
                    input.type !== 'color' &&
                    input.type !== 'checkbox' &&
                    input.tagName !== 'SELECT'
                ) {
                    return;
                }

                const index = Number(input.dataset.index);
                const key = input.dataset.key;

                let value;

                if (input.type === 'checkbox') {
                    value = input.checked;
                } else if (
                    input.type === 'color' ||
                    input.tagName === 'SELECT'
                ) {
                    value = input.value;
                } else {
                    value = Number(input.value);
                }

                if (Number.isNaN(index) || !key) {
                    return;
                }

                if (
                    input.type === 'number' &&
                    Number.isNaN(value)
                ) {
                    return;
                }

                this.positions[index][key] = value;

                this.render();
            });

            controls.addEventListener('wheel', (event) => {
                const input = event.target;

                if (input.type !== 'number') {
                    return;
                }

                if (document.activeElement !== input) {
                    return;
                }

                event.preventDefault();

                const step = event.shiftKey ? 10 : 1;
                const direction = event.deltaY < 0 ? 1 : -1;
                const nextValue = Number(input.value) + direction * step;

                input.value = nextValue;

                input.dispatchEvent(
                    new Event('input', { bubbles: true })
                );
            }, { passive: false });

            controls.addEventListener('click', (event) => {
                const button = event.target;

                if (button.dataset.copyAll) {
                    navigator.clipboard.writeText(
                        JSON.stringify(this.positions, null, 4)
                    );

                    return;
                }

                if (button.dataset.copyField) {
                    const index = Number(button.dataset.copyField);

                    navigator.clipboard.writeText(
                        JSON.stringify(this.positions[index], null, 4)
                    );

                    return;
                }

                if (button.dataset.copyKey) {
                    const index = Number(button.dataset.index);
                    const key = button.dataset.copyKey;

                    navigator.clipboard.writeText(
                        JSON.stringify(this.positions[index][key])
                    );
                }
            });

            document.body.appendChild(controls);
        }

        wrapText( context, text, maxWidth, letterSpacing = 0, maxCharactersPerLine = null ) {
            const paragraphs = text.split('\n');
            const lines = [];

            for ( let p = 0; p < paragraphs.length; p++ ) {
                const words = paragraphs[p].split(' ');
                let currentLine = '';

                for ( let i = 0; i < words.length; i++ ) {
                    const word = words[i];

                    if ( maxCharactersPerLine && word.length > maxCharactersPerLine ) {
                        if ( currentLine ) {
                            lines.push( currentLine );
                            currentLine = '';
                        }

                        for ( let start = 0; start < word.length; start += maxCharactersPerLine ) {
                            const chunk = word.slice( start, start + maxCharactersPerLine );

                            if ( chunk.length === maxCharactersPerLine ) {
                                lines.push( chunk );
                            } else {
                                currentLine = chunk;
                            }
                        }

                        continue;
                    }

                    const testLine = currentLine ? `${currentLine} ${word}` : word;
                    const testWidth = this.measureTextWidth( context, testLine, letterSpacing );

                    const exceedsWidth = testWidth > maxWidth && currentLine;
                    const exceedsCharacters =
                        maxCharactersPerLine &&
                        testLine.length > maxCharactersPerLine &&
                        currentLine;

                    if ( exceedsWidth || exceedsCharacters ) {
                        lines.push( currentLine );
                        currentLine = word;
                    } else {
                        currentLine = testLine;
                    }
                }

                if ( currentLine ) {
                    lines.push( currentLine );
                }
            }

            return lines;
        }

        measureTextWidth( context, text, letterSpacing = 0 ) {
            const baseWidth = context.measureText(text).width;

            if ( !letterSpacing || text.length <= 1 ) {
                return baseWidth;
            }

            return baseWidth + letterSpacing * ( text.length - 1 );
        }

        drawTextLine(context, text, x, y, letterSpacing = 0) {
            if ( !letterSpacing ) {
                context.fillText( text, x, y );
                return;
            }

            let currentX = x;
            const originalTextAlign = context.textAlign;

            if ( originalTextAlign === 'center' ) {
                currentX = x - this.measureTextWidth(context, text, letterSpacing) / 2;
            }

            if ( originalTextAlign === 'right' || originalTextAlign === 'end' ) {
                currentX = x - this.measureTextWidth(context, text, letterSpacing);
            }

            context.textAlign = 'left';

            for ( let index = 0; index < text.length; index++ ) {
                const character = text[ index ];

                context.fillText( character, currentX, y );
                currentX += context.measureText( character ).width + letterSpacing;
            }

            context.textAlign = originalTextAlign;
        }

        drawWrappedText({
            context,
            text,
            x,
            y,
            maxWidth,
            lineHeight,
            letterSpacing,
            maxCharactersPerLine
        }) {
            const lines = this.wrapText(
                context,
                text,
                maxWidth,
                letterSpacing,
                maxCharactersPerLine
            );

            for ( let index = 0; index < lines.length; index++ ) {
                this.drawTextLine(
                    context,
                    lines[index],
                    x,
                    y + index * lineHeight,
                    letterSpacing
                );
            }
        }

        drawTextBox({
            context,
            x,
            y,
            maxWidth,
            lineHeight,
            textAlign,
            text,
            letterSpacing = 0,
            maxCharactersPerLine = null
        }) {
            const lines = this.wrapText(
                context,
                text,
                maxWidth,
                letterSpacing,
                maxCharactersPerLine
            );
            const height = lines.length * lineHeight;

            let boxX = x;

            if (textAlign === 'center') {
                boxX = x - maxWidth / 2;
            }

            if (textAlign === 'right' || textAlign === 'end') {
                boxX = x - maxWidth;
            }

            context.save();
            context.strokeStyle = '#ff0000';
            context.lineWidth = 2;
            context.setLineDash([8, 6]);

            context.strokeRect(
                boxX,
                y - lineHeight,
                maxWidth,
                height + lineHeight * 0.25
            );

            context.restore();
        }

        getDebugInputMarkup(index, key, label, value, step = 1) {
            return `
                <label style="display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #333;">
                    <span style="font-weight: 600;">${label}</span>

                    <div style="display: flex; gap: 6px;">
                        <input
                            type="number"
                            value="${value}"
                            step="${step}"
                            data-index="${index}"
                            data-key="${key}"
                            style="width: 100%; min-width: 0; padding: 7px 8px; border: 1px solid #ccc; border-radius: 7px;"
                        >

                        <button
                            type="button"
                            data-index="${index}"
                            data-copy-key="${key}"
                            style="padding: 7px 8px; border: 1px solid #bbb; border-radius: 7px; background: #f7f7f7; cursor: pointer; white-space: nowrap;"
                        >
                            Copy
                        </button>
                    </div>
                </label>
            `;
        }

        getDebugFontWeightMarkup(index, value) {
            return `
                <label style="display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #333;">
                    <span style="font-weight: 600;">Font Weight</span>

                    <div style="display: flex; gap: 6px;">
                        <select
                            data-index="${index}"
                            data-key="fontWeight"
                            style="width: 100%; min-width: 0; padding: 7px 8px; border: 1px solid #ccc; border-radius: 7px;"
                        >
                            <option value="100" ${value == 100 ? 'selected' : ''}>100</option>
                            <option value="200" ${value == 200 ? 'selected' : ''}>200</option>
                            <option value="300" ${value == 300 ? 'selected' : ''}>300</option>
                            <option value="400" ${value == 400 ? 'selected' : ''}>400</option>
                            <option value="500" ${value == 500 ? 'selected' : ''}>500</option>
                            <option value="600" ${value == 600 ? 'selected' : ''}>600</option>
                            <option value="700" ${value == 700 ? 'selected' : ''}>700</option>
                            <option value="800" ${value == 800 ? 'selected' : ''}>800</option>
                            <option value="900" ${value == 900 ? 'selected' : ''}>900</option>
                        </select>

                        <button
                            type="button"
                            data-index="${index}"
                            data-copy-key="fontWeight"
                            style="padding: 7px 8px; border: 1px solid #bbb; border-radius: 7px; background: #f7f7f7; cursor: pointer; white-space: nowrap;"
                        >
                            Copy
                        </button>
                    </div>
                </label>
            `;
        }

        getDebugColorMarkup(index, value) {
            return `
                <label style="display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #333;">
                    <span style="font-weight: 600;">Color</span>

                    <div style="display: flex; gap: 6px;">
                        <input
                            type="color"
                            value="${value}"
                            data-index="${index}"
                            data-key="color"
                            style="width: 100%; min-width: 0; height: 38px; padding: 3px; border: 1px solid #ccc; border-radius: 7px;"
                        >

                        <button
                            type="button"
                            data-index="${index}"
                            data-copy-key="color"
                            style="padding: 7px 8px; border: 1px solid #bbb; border-radius: 7px; background: #f7f7f7; cursor: pointer; white-space: nowrap;"
                        >
                            Copy
                        </button>
                    </div>
                </label>
            `;
        }

        getAvailableFonts() {
            const fonts = [];

            for (let index = 0; index < this.positions.length; index++) {
                const fontFamily = this.positions[index].fontFamily;

                if (fontFamily && !fonts.includes(fontFamily)) {
                    fonts.push(fontFamily);
                }
            }

            return fonts;
        }

        getDebugFontFamilyMarkup(index, value) {
            const fonts = this.getAvailableFonts();

            return `
                <label style="display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #333;">
                    <span style="font-weight: 600;">Font Family</span>

                    <div style="display: flex; gap: 6px;">
                        <select
                            data-index="${index}"
                            data-key="fontFamily"
                            style="width: 100%; min-width: 0; padding: 7px 8px; border: 1px solid #ccc; border-radius: 7px;"
                        >
                            ${fonts.map((font) => `
                                <option value="${font}" ${font === value ? 'selected' : ''}>
                                    ${font}
                                </option>
                            `).join('')}
                        </select>

                        <button
                            type="button"
                            data-index="${index}"
                            data-copy-key="fontFamily"
                            style="padding: 7px 8px; border: 1px solid #bbb; border-radius: 7px; background: #f7f7f7; cursor: pointer; white-space: nowrap;"
                        >
                            Copy
                        </button>
                    </div>
                </label>
            `;
        }

        getDebugCheckboxMarkup(index, key, label, value) {
            return `
                <label style="display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #333;">
                    <span style="font-weight: 600;">${label}</span>

                    <input
                        type="checkbox"
                        ${value ? 'checked' : ''}
                        data-index="${index}"
                        data-key="${key}"
                        style="width: 20px; height: 20px;"
                    >
                </label>
            `;
        }
    }

    customElements.define('customization-canvas', CustomizationCanvas);
}